import type { Deal } from "@/api/client";
import { DEAL_DETAIL_STATUS } from "@/components/deals/dealDetailStatus";
import { Field } from "@/components/deals/DealProfileSections";
import { Badge } from "@/components/ui/badge";
import { closeReasonText, declinedOutcomeLeadIn } from "@/lib/deal-labels";

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString("en-AU") : "—";
}

/** Date + time for the auto-approval notice (CHP-333). */
function formatDateTime(value: string | null): string {
  return value
    ? new Date(value).toLocaleString("en-AU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";
}

/**
 * The "Status" section of the deal detail view (RACCPE-21): a horizontal row
 * of the status pill + the per-status dates, plus an optional rationale block.
 * `viewer="manager"` additionally surfaces the reviewer's name where the
 * status carries one. Driven entirely by DEAL_DETAIL_STATUS.
 */
export function DealStatusSection({ deal, viewer }: { deal: Deal; viewer: "partner" | "manager" }) {
  const config = DEAL_DETAIL_STATUS[deal.deal_status];
  return (
    <section>
      <h2 className="mb-3 text-base font-semibold">Status</h2>
      <dl className="flex flex-wrap items-start gap-x-10 gap-y-3">
        <div>
          {/* CHP-345: the section <h2>Status</h2> already labels this; the pill
              stands alone, with no redundant inner "Status" field label. */}
          <dd>
            <Badge variant={config.pillVariant}>{config.pillLabel}</Badge>
          </dd>
        </div>
        {config.dates.map((d) => (
          <Field key={d.field} label={d.label} value={formatDate(deal[d.field])} />
        ))}
        {viewer === "manager" && config.showReviewer ? (
          deal.reviewer_name ? (
            <Field label="Reviewer" value={deal.reviewer_name} />
          ) : deal.auto_approved ? (
            // CHP-356: SLA-lapse auto-approval has no human reviewer. (A CM/RA/RM
            // on-behalf registration is NOT auto_approved — it carries the
            // registering manager as the real reviewer above.)
            <Field label="Reviewer" value="None (Auto-approved)" />
          ) : null
        ) : null}
      </dl>
      {config.rationale === "review_reason" && deal.review_reason ? (
        // CHP-777: standardise to "<Outcome> declined. Reason: <text>". The
        // review_reason rationale only fires for the denied (registration
        // declined) status, so the lead-in is fixed.
        <Rationale
          label={declinedOutcomeLeadIn("registration_declined") ?? "Reason for decision"}
          text={`Reason: ${deal.review_reason}`}
        />
      ) : null}
      {config.rationale === "close" ? (
        <Rationale label="Rationale" text={closeReasonText(deal)} />
      ) : null}
      {/* CHP-778: a declined closed-won puts the deal back to Live — say so
          under the status (both viewers) so the pill change isn't mistaken for
          a silent revert. Copy is the ticket's, verbatim. Hidden again the
          moment the status leaves LIVE (e.g. a later approved closed-won). */}
      {deal.deal_status === "live" && deal.last_outcome === "closed_won_declined" ? (
        <div
          className="mt-4 rounded-md border border-border bg-muted/40 p-3"
          data-testid="deal-status-back-to-live"
        >
          <p className="text-sm">
            A closed-won submission for this deal was declined and it is back to Live. Reason noted
            below in Deal Updates.
          </p>
        </div>
      ) : null}
      {/* CHP-333: surface auto-approval (SLA lapse) and when it happened.
          CHP-390: CM/RA/RM only — BDM/PO must not see auto-approval references
          on their detail view. */}
      {viewer === "manager" && deal.auto_approved ? (
        <div className="mt-4 rounded-md border border-border bg-muted/40 p-3">
          <p className="text-sm">
            This deal was <span className="font-medium">auto-approved</span> on{" "}
            <span className="font-medium">
              {formatDateTime(deal.auto_approved_at ?? deal.reviewed_at)}
            </span>{" "}
            after the review SLA lapsed.
          </p>
        </div>
      ) : null}
    </section>
  );
}

function Rationale({ label, text }: { label: string; text: string }) {
  return (
    <div className="mt-4">
      <dt className="text-sm font-medium">{label}</dt>
      <dd className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">{text}</dd>
    </div>
  );
}
